import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import restaurantService, {
  MenuItemDetail as MenuItemDetailType,
  Restaurant,
  Review,
} from '../../services/restaurantService';
import cartService, { apiErrorMessage, CartItem, CartOptions } from '../../services/cartService';
import { cartOptionsFor, cartOptionsForLine, findCartLine } from '../../utils/cartLines';
import { getAddonsAmount, lineUnitPrice } from '../../utils/menuOptions';
import { useGuestNoteSettings } from '../../hooks/useGuestNoteSettings';
import MenuOrderOptions, {
  getInitialSelections,
  MenuOrderSelections,
  validateOrderSelections,
} from '../common/MenuOrderOptions/MenuOrderOptions';
import { getDisplayMrp, getDisplayPrice, getEffectiveDisplayPrice, getStrikeThroughPrice, roundDisplayPrice } from '../../utils/menuPricing';
import wishlistService from '../../services/wishlistService';
import { isWishlistLoginRequired } from '../../utils/wishlistAuth';
import LoginModal from '../LoginModal/LoginModal';
import { hasPendingSocialAuthError } from '../../utils/socialAuth';
import MessageDialog from '../common/MessageDialog/MessageDialog';
import { DEFAULT_IMAGES, DEFAULT_USER_AVATAR, getMenuItemImageUrl } from '../../constants/images';
import Header from '../Header/Header';
import MenuItemDetailSkeleton from './MenuItemDetailSkeleton';
import Footer from '../Footer/Footer';
import PageContainer from '../PageContainer/PageContainer';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import { getRestaurantBreadcrumbLabel, restaurantHomePath } from '../../utils/restaurantBreadcrumb';
import { IconCart, IconCartFab } from '../RestaurantMenu/MenuListingIcons';
import { CART_UPDATED_EVENT } from '../../utils/cartEvents';
import { useAppSelector } from '../../store/hooks';
import { useGuestOrderType } from '../../hooks/useGuestOrderType';
import { withGuestOrderTypeQuery } from '../../utils/guestOrderTypeStorage';
import { useGuestCanOrder } from '../../hooks/useGuestCanOrder';
import GuestOrderingBanner from '../common/GuestOrderingBanner/GuestOrderingBanner';
import { guestOrderingMessage, GuestOrderingStatus, isAcceptingGuestOrders } from '../../utils/guestOrdering';
import { useSsrPreload, matchSsrPreload } from '../../ssr/preload';
import SeoHead from '../Seo/SeoHead';
import {
  buildMenuItemPageTitle,
  buildMenuItemPageDescription,
  shouldIndexMenuItemPage,
} from '../../utils/seoMetadata';
import './MenuItemDetail.css';

const MenuItemDetail: React.FC = () => {
  const { appName, itemUrl } = useParams<{ appName: string; itemUrl: string }>();
  const { user, isAuthenticated } = useAppSelector((state) => state.user);
  const guestOrderType = useGuestOrderType(appName);
  const { canOrder } = useGuestCanOrder(appName);
  const noteSettings = useGuestNoteSettings(appName);
  const preload = useSsrPreload();
  const itemPreload = matchSsrPreload(preload, 'item', appName, itemUrl) ? preload : null;
  
  const [menuItem, setMenuItem] = useState<MenuItemDetailType | null>(itemPreload?.menuItem ?? null);
  const [guestOrdering, setGuestOrdering] = useState<GuestOrderingStatus | null>(itemPreload?.guestOrdering ?? null);
  const [reviews, setReviews] = useState<Review[]>((itemPreload?.reviews as Review[]) ?? []);
  const [loading, setLoading] = useState(!itemPreload?.menuItem);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(0);
  const [selections, setSelections] = useState<MenuOrderSelections>({});
  const [cartLinesForMenu, setCartLinesForMenu] = useState<CartItem[]>([]);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [reviewForm, setReviewForm] = useState({
    comment: '',
    rating: 5,
    title: '',
  });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewEligible, setReviewEligible] = useState<boolean | null>(null);
  const [reviewEligibilityMessage, setReviewEligibilityMessage] = useState('');
  const [cartCount, setCartCount] = useState(0);
  const [showLoginModal, setShowLoginModal] = useState(() => hasPendingSocialAuthError());
  const [messageDialog, setMessageDialog] = useState<{
    title: string;
    message: string;
    variant?: 'info' | 'error';
  } | null>(null);
  const loginSuccessCallbackRef = useRef<(() => void) | null>(null);
  const loadGenerationRef = useRef(0);
  // One cart request at a time: +/− are disabled until it settles and the quantity is resynced.
  const cartPendingRef = useRef(false);
  const [cartPending, setCartPending] = useState(false);

  const applyCartResponse = useCallback((menuId: number | undefined, cartItems?: CartItem[], count?: number) => {
    setCartCount(count ?? cartItems?.length ?? 0);
    if (menuId && cartItems) {
      setCartLinesForMenu(cartItems.filter((line) => line.menuId === menuId));
    } else {
      setCartLinesForMenu([]);
    }
  }, []);

  const refreshCartState = useCallback(async (menuId?: number) => {
    if (!appName) return;
    try {
      const response = await cartService.getCart(appName);
      if (response.success) {
        applyCartResponse(
          menuId ?? menuItem?.id,
          response.cartItems,
          response.cartDetails?.totalItemCount ?? response.cartItems?.length ?? 0
        );
      }
    } catch {
      if (menuId ?? menuItem?.id) {
        setCartLinesForMenu([]);
      }
    }
  }, [appName, menuItem?.id, applyCartResponse]);

  useEffect(() => {
    document.body.classList.add('menu-item-detail-page-active');
    return () => document.body.classList.remove('menu-item-detail-page-active');
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!appName || !itemUrl) return undefined;

    const generation = ++loadGenerationRef.current;
    let cancelled = false;

    if (itemPreload?.menuItem && itemPreload.appName === appName && itemPreload.itemUrl === itemUrl) {
      return undefined;
    }

    const loadPage = async () => {
      setLoading(true);
      setError(null);
      try {
        const [itemRes, reviewsRes, cartRes, restRes] = await Promise.all([
          restaurantService.getMenuItemDetail(appName, itemUrl),
          restaurantService.getMenuItemReviews(appName, itemUrl),
          cartService.getCart(appName),
          restaurantService.getRestaurant(appName),
        ]);

        if (cancelled || generation !== loadGenerationRef.current) return;

        setGuestOrdering(restRes.guestOrdering ?? null);

        if (itemRes.success && itemRes.menuItem) {
          setMenuItem(itemRes.menuItem);
          setSelections(getInitialSelections(itemRes.menuItem));
          if (cartRes.success) {
            applyCartResponse(
              itemRes.menuItem.id,
              cartRes.cartItems,
              cartRes.cartDetails?.totalItemCount ?? cartRes.cartItems?.length ?? 0
            );
          }
        } else {
          setMenuItem(null);
          setError(itemRes.message || 'Menu item not found');
        }

        if (reviewsRes.success && reviewsRes.reviews) {
          setReviews(reviewsRes.reviews);
        } else {
          setReviews([]);
        }
      } catch (err: any) {
        if (!cancelled && generation === loadGenerationRef.current) {
          setError(err.response?.data?.message || 'Failed to load menu item');
        }
      } finally {
        if (!cancelled && generation === loadGenerationRef.current) {
          setLoading(false);
        }
      }
    };

    void loadPage();
    return () => {
      cancelled = true;
    };
  }, [appName, itemUrl, applyCartResponse]);

  useEffect(() => {
    if (!user?.id || !appName || !itemUrl) {
      setReviewEligible(null);
      setReviewEligibilityMessage('');
      return undefined;
    }

    let cancelled = false;
    restaurantService
      .getMenuItemReviewEligibility(appName, itemUrl)
      .then((response) => {
        if (cancelled) return;
        if (response.success) {
          setReviewEligible(!!response.eligible);
          setReviewEligibilityMessage(response.message || '');
        }
      })
      .catch(() => {
        if (!cancelled) setReviewEligible(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id, appName, itemUrl]);

  useEffect(() => {
    if (!appName) return undefined;

    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ appName: string; count?: number }>).detail;
      if (detail?.appName !== appName) return;
      if (typeof detail.count === 'number') {
        setCartCount(detail.count);
        return;
      }
      void refreshCartState();
    };

    window.addEventListener(CART_UPDATED_EVENT, handler);
    return () => window.removeEventListener(CART_UPDATED_EVENT, handler);
  }, [appName, refreshCartState]);

  useEffect(() => {
    if (!menuItem?.id) {
      return;
    }
    const line = findCartLine(cartLinesForMenu, menuItem.id, selections, menuItem);
    setQuantity(line?.quantity ?? 0);
  }, [cartLinesForMenu, menuItem, selections]);

  const reloadMenuItemSilent = async () => {
    if (!appName || !itemUrl) return;
    try {
      const response = await restaurantService.getMenuItemDetail(appName, itemUrl);
      if (response.success && response.menuItem) {
        setMenuItem(response.menuItem);
      }
    } catch (err) {
      console.error('Failed to refresh menu item:', err);
    }
  };

  const loadReviews = async () => {
    if (!appName || !itemUrl) return;
    try {
      const response = await restaurantService.getMenuItemReviews(appName, itemUrl);
      if (response.success && response.reviews) {
        setReviews(response.reviews);
      }
    } catch (err) {
      console.error('Failed to load reviews:', err);
    }
  };

  const runCartRequest = async (task: () => Promise<void>) => {
    if (cartPendingRef.current) return;
    cartPendingRef.current = true;
    setCartPending(true);
    try {
      await task();
    } finally {
      cartPendingRef.current = false;
      setCartPending(false);
    }
  };

  const handleIncrement = () =>
    runCartRequest(async () => {
      if (!menuItem || !appName) return;
      const err = validateOrderSelections(menuItem, selections, noteSettings.itemNoteMaxLength);
      if (err) {
        setOptionsError(err);
        return;
      }
      setOptionsError(null);
      const newQty = quantity + 1;
      setQuantity(newQty);
      await addToCart(menuItem.id, newQty, cartOptions());
    });

  const handleDecrement = () =>
    runCartRequest(async () => {
      if (!menuItem || !appName || quantity <= 0) return;
      const newQty = quantity - 1;
      setQuantity(newQty);
      await addToCart(menuItem.id, newQty, cartOptions());
    });

  const addToCart = async (menuId: number, qty: number, options?: CartOptions) => {
    if (!appName) return;
    if (!isAcceptingGuestOrders(guestOrdering)) {
      setMessageDialog({
        title: 'Orders paused',
        message: guestOrderingMessage(guestOrdering) || 'This restaurant is not accepting customer orders right now.',
        variant: 'info',
      });
      return;
    }
    try {
      const response = await cartService.addToCart(appName, menuId, qty, guestOrderType, options);
      if (!response.success) {
        // Invalid variant / add-on / note (400): show why and resync the quantity.
        setOptionsError(response.message || 'Could not update your cart');
      }
      await refreshCartState(menuId);
    } catch (err) {
      setOptionsError(apiErrorMessage(err, 'Could not update your cart'));
      await refreshCartState(menuId);
    }
  };

  // A line already in the cart is addressed with its stored identity; a new one from the picker.
  const cartOptions = (): CartOptions => {
    const line = menuItem ? findCartLine(cartLinesForMenu, menuItem.id, selections, menuItem) : undefined;
    return line ? cartOptionsForLine(line) : cartOptionsFor(selections, menuItem);
  };

  const tryAddToCart = (qty: number) => {
    if (!menuItem) return;
    const err = validateOrderSelections(menuItem, selections, noteSettings.itemNoteMaxLength);
    if (err) {
      setOptionsError(err);
      return;
    }
    setOptionsError(null);
    void runCartRequest(() => addToCart(menuItem.id, qty, cartOptions()));
  };

  const handleToggleWishlist = async () => {
    if (!menuItem || !appName) return;

    const runToggle = async () => {
      if (!menuItem || !appName) return;
      try {
        const currentState = menuItem.inWishlist || false;
        const response = await wishlistService.toggleWishlist(
          appName,
          menuItem.id,
          !currentState,
          menuItem.wishlistId
        );
        if (response.success) {
          await reloadMenuItemSilent();
          return;
        }
        if (isWishlistLoginRequired(undefined, response.message)) {
          loginSuccessCallbackRef.current = () => {
            void runToggle();
          };
          setShowLoginModal(true);
          return;
        }
        setMessageDialog({
          title: 'Wishlist',
          message: response.message || 'Could not update your wishlist.',
          variant: 'error',
        });
      } catch (err: any) {
        const data = err.response?.data;
        if (isWishlistLoginRequired(err.response?.status, data?.message, data?.requiresLogin)) {
          loginSuccessCallbackRef.current = () => {
            void runToggle();
          };
          setShowLoginModal(true);
          return;
        }
        setMessageDialog({
          title: 'Wishlist',
          message: data?.message || 'Could not update your wishlist.',
          variant: 'error',
        });
      }
    };

    if (!isAuthenticated) {
      loginSuccessCallbackRef.current = () => {
        void runToggle();
      };
      setShowLoginModal(true);
      return;
    }

    await runToggle();
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!appName || !itemUrl) return;
    
    setSubmittingReview(true);
    try {
      const response = await restaurantService.saveMenuItemReview(appName, itemUrl, reviewForm);
      if (response.success) {
        setReviewForm({
          comment: '',
          rating: 5,
          title: '',
        });
        setReviewEligible(false);
        setReviewEligibilityMessage('You have already reviewed this item');
        await loadReviews();
        alert(response.message || 'Review submitted and pending moderation');
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const formatPrice = (price: number): string => {
    const rounded = roundDisplayPrice(price);
    if (!menuItem?.restaurant?.currencySymbol) {
      return `₹${rounded}`;
    }
    const decoded = new DOMParser().parseFromString(menuItem.restaurant.currencySymbol, 'text/html').body.textContent || menuItem.restaurant.currencySymbol;
    return `${decoded}${rounded}`;
  };

  const getImageUrl = (imageName: string): string => {
    if (!menuItem || !menuItem.restaurant || !menuItem.id || imageName === 'default') {
      return DEFAULT_IMAGES.MENU_ITEM;
    }
    return getMenuItemImageUrl(menuItem.restaurant.id, menuItem.id, imageName);
  };

  const isVideoMedia = (mimeType?: string, name?: string): boolean => {
    if (mimeType && mimeType.startsWith('video/')) return true;
    if (name) {
      const lower = name.toLowerCase();
      return lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.mov');
    }
    return false;
  };

  const formatPrepTime = (minutes?: number): string | null => {
    if (minutes == null || minutes <= 0) return null;
    return `${minutes} min prep`;
  };

  const getItemTypeLabel = (itemType?: string): string | null => {
    if (!itemType) return null;
    const normalized = itemType.toLowerCase();
    if (normalized === 'veg' || normalized === 'vegetarian') return 'Veg';
    if (normalized === 'nonveg' || normalized === 'non-veg' || normalized === 'non vegetarian') {
      return 'Non-Veg';
    }
    return itemType;
  };

  const footerRestaurant = useMemo((): Restaurant | null => {
    if (!menuItem?.restaurant) return null;
    return {
      id: menuItem.restaurant.id,
      appName: menuItem.restaurant.appName,
      restaurantName: menuItem.restaurant.restaurantName || appName || '',
      currencySymbol: menuItem.restaurant.currencySymbol,
    };
  }, [menuItem?.restaurant, appName]);

  if (loading) {
    return <MenuItemDetailSkeleton />;
  }

  if (error || !menuItem) {
    return (
      <div className="menu-item-detail-page">
        <Header />
        <PageContainer>
          <div className="menu-item-detail-shell menu-item-detail-state">
            <div className="menu-item-detail-error">
              <p>{error || 'Menu item not found'}</p>
              <Link to={`/restaurants/${appName}/menu`} className="back-link">
                ← Back to Menu
              </Link>
            </div>
          </div>
        </PageContainer>
        <Footer variant="restaurant" restaurant={footerRestaurant} />
      </div>
    );
  }

  const images = menuItem.images && menuItem.images.length > 0 
    ? menuItem.images 
    : [{ id: 0, name: 'default' }];
  
  const sellingPrice = getDisplayPrice(menuItem, {
    sizeTypeId: selections.sizeTypeId,
  });
  const effectivePrice = getEffectiveDisplayPrice(menuItem, {
    sizeTypeId: selections.sizeTypeId,
  });
  // Add-ons are billed in full on top of the (discounted) variant price (design D3/D6).
  const addonsAmount = getAddonsAmount(menuItem, selections);
  const displayMrp = getDisplayMrp(menuItem, selections.sizeTypeId);
  const strikeThroughPrice = getStrikeThroughPrice(menuItem, {
    sizeTypeId: selections.sizeTypeId,
  });
  // Unit = variant + add-ons rounded once (same as the options sheet and the server's unit price).
  const currentUnitPrice = lineUnitPrice(effectivePrice, addonsAmount);
  const strikeUnitPrice =
    strikeThroughPrice != null ? lineUnitPrice(strikeThroughPrice, addonsAmount) : undefined;
  const isAvailable = menuItem.availability !== false;

  const discountPercentage =
    strikeThroughPrice && strikeThroughPrice > effectivePrice
      ? Math.round(((strikeThroughPrice - effectivePrice) / strikeThroughPrice) * 100)
      : menuItem.discountPercent != null && menuItem.discountPercent > 0
        ? Math.round(menuItem.discountPercent)
        : displayMrp && displayMrp > sellingPrice
          ? Math.round(((displayMrp - sellingPrice) / displayMrp) * 100)
          : 0;

  const savingsAmount =
    strikeThroughPrice && strikeThroughPrice > effectivePrice
      ? roundDisplayPrice(strikeThroughPrice - effectivePrice)
      : displayMrp && displayMrp > sellingPrice
        ? roundDisplayPrice(displayMrp - sellingPrice)
        : 0;

  const prepTimeLabel = formatPrepTime(menuItem.preparationTime);
  const itemTypeLabel = getItemTypeLabel(menuItem.itemType);
  const restaurantName = menuItem.restaurant?.restaurantName;
  const hasInfoPills =
  !!prepTimeLabel ||
    !!itemTypeLabel ||
    (menuItem.rating != null && menuItem.rating > 0) ||
    !!menuItem.category ||
    !!menuItem.cuisine ||
    (menuItem.tags && menuItem.tags.length > 0);

  return (
    <div className="menu-item-detail-page">
      <SeoHead
        title={buildMenuItemPageTitle(menuItem, menuItem.restaurant)}
        description={buildMenuItemPageDescription(menuItem, menuItem.restaurant)}
        path={appName && itemUrl ? `/restaurants/${appName}/menu/${itemUrl}` : undefined}
        noIndex={!shouldIndexMenuItemPage(menuItem)}
        images={
          menuItem.restaurant?.id
            ? [getImageUrl(images.find((img) => img.primaryCheck)?.name || images[0]?.name || 'default')]
            : undefined
        }
      />
      <Header />
      <PageContainer>
        <div className="menu-item-detail-shell">
        <Breadcrumb
          items={[
            {
              label: getRestaurantBreadcrumbLabel(menuItem.restaurant?.restaurantName, appName),
              path: restaurantHomePath(appName),
            },
            { label: 'Menu', path: `/restaurants/${appName}/menu` },
            { label: menuItem.item, active: true },
          ]}
        />

        <GuestOrderingBanner status={guestOrdering} compact />

        <div className="menu-item-detail-main">
          <div className="menu-item-detail-grid">
            <div className="menu-item-gallery">
              <div className="gallery-main">
                {isVideoMedia(
                  images[selectedImageIndex]?.mimeType,
                  images[selectedImageIndex]?.name
                ) ? (
                  <video
                    className="main-image main-video"
                    src={getImageUrl(images[selectedImageIndex]?.name || 'default')}
                    controls
                    playsInline
                    muted
                  />
                ) : (
                  <img
                    src={getImageUrl(images[selectedImageIndex]?.name || 'default')}
                    alt={menuItem.item}
                    className="main-image"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = DEFAULT_IMAGES.MENU_ITEM;
                    }}
                  />
                )}
                {discountPercentage > 0 && (
                  <span className="gallery-discount-badge">{discountPercentage}% off</span>
                )}
                <button
                  type="button"
                  className={`gallery-bookmark ${menuItem.inWishlist ? 'active' : ''}`}
                  onClick={handleToggleWishlist}
                  aria-label={menuItem.inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
                >
                  <i className={`fa ${menuItem.inWishlist ? 'fa-bookmark' : 'fa-bookmark-o'}`} />
                </button>
              </div>
              {images.length > 1 && (
                <div className="gallery-thumbs">
                  {images.map((image, index) => (
                    <div
                      key={image.id}
                      className={`gallery-thumb ${index === selectedImageIndex ? 'active' : ''}`}
                      onClick={() => setSelectedImageIndex(index)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setSelectedImageIndex(index);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      {isVideoMedia(image.mimeType, image.name) ? (
                        <video
                          className="thumbnail-video"
                          src={getImageUrl(image.name)}
                          muted
                          playsInline
                        />
                      ) : (
                        <img
                          src={getImageUrl(image.name)}
                          alt={`${menuItem.item} ${index + 1}`}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = DEFAULT_IMAGES.MENU_ITEM;
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="menu-item-product-info">
              {restaurantName && (
                <div className="restaurant-tag">{restaurantName}</div>
              )}

              <h1 className="product-name">{menuItem.item}</h1>

              {menuItem.description && (
                <p className="product-desc">{menuItem.description}</p>
              )}

              <div className="status-row">
                <span className={`badge-stock ${isAvailable ? 'in-stock' : 'out-of-stock'}`}>
                  {isAvailable ? 'In Stock' : 'Out of Stock'}
                </span>
              </div>

              {hasInfoPills && (
                <div className="info-pills">
                  {prepTimeLabel && (
                    <span className="info-pill">
                      <i className="fa fa-clock-o" aria-hidden="true" />
                      {prepTimeLabel}
                    </span>
                  )}
                  {menuItem.category && (
                    <span className="info-pill">
                      <i className="fa fa-th-large" aria-hidden="true" />
                      {menuItem.category}
                    </span>
                  )}
                  {menuItem.cuisine && (
                    <span className="info-pill">
                      <i className="fa fa-globe" aria-hidden="true" />
                      {menuItem.cuisine}
                    </span>
                  )}
                  {menuItem.tags?.map((tag) => (
                    <span key={tag} className="info-pill">
                      <i className="fa fa-tag" aria-hidden="true" />
                      {tag}
                    </span>
                  ))}
                  {menuItem.rating != null && menuItem.rating > 0 && (
                    <span className="info-pill">
                      <i className="fa fa-star" aria-hidden="true" />
                      {menuItem.rating.toFixed(1)} rating
                    </span>
                  )}
                  {itemTypeLabel && (
                    <span className="info-pill">
                      <i
                        className={`fa fa-${itemTypeLabel === 'Veg' ? 'leaf' : 'cutlery'}`}
                        aria-hidden="true"
                      />
                      {itemTypeLabel}
                    </span>
                  )}
                </div>
              )}

              {menuItem.ingredients && menuItem.ingredients.length > 0 && (
                <div className="ingredients-section">
                  <div className="ingredients-label">Ingredients</div>
                  <div className="ingredient-chips">
                    {menuItem.ingredients.map((ingredient) => (
                      <span key={ingredient.id} className="ingredient-chip">
                        {ingredient.ingredientName}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <MenuOrderOptions
                item={menuItem}
                selections={selections}
                onChange={(next) => {
                  setOptionsError(null);
                  setSelections(next);
                }}
                formatPrice={formatPrice}
                noteSettings={noteSettings}
              />
              {optionsError && <p className="menu-order-options-error">{optionsError}</p>}

              {menuItem.promotionBadge && (
                <div className="detail-promo-offer">
                  <span className="detail-promo-offer__badge">{menuItem.promotionBadge}</span>
                  {menuItem.promotionName ? (
                    <span className="detail-promo-offer__text">{menuItem.promotionName}</span>
                  ) : null}
                </div>
              )}

              <div className="detail-card-price-row">
                {strikeThroughPrice && strikeThroughPrice > effectivePrice && (
                  <span className="price-original">{formatPrice(strikeUnitPrice ?? strikeThroughPrice)}</span>
                )}
                <span className="price-current">{formatPrice(currentUnitPrice)}</span>
                {strikeThroughPrice && strikeThroughPrice > effectivePrice && discountPercentage > 0 && (
                  <span className="price-badge">{discountPercentage}% off</span>
                )}
              </div>
              {savingsAmount > 0 && (
                <p className="price-savings">
                  You save {formatPrice(savingsAmount)} on this item
                </p>
              )}

              {!isAvailable && (
                <div className="out-of-stock-banner">
                  This item is currently out of stock
                </div>
              )}

              {isAvailable && canOrder && (
                <>
                  <div className="qty-section">
                    <div className="qty-label">Quantity</div>
                    <div className="quantity-controls">
                      <button
                        type="button"
                        className="qty-btn decrement"
                        onClick={handleDecrement}
                        disabled={quantity === 0 || cartPending}
                        aria-busy={cartPending}
                        aria-label="Decrease quantity"
                      >
                        <i className="fa fa-minus" aria-hidden="true" />
                      </button>
                      <input
                        type="text"
                        value={quantity}
                        readOnly
                        className="qty-input"
                        aria-label="Quantity"
                      />
                      <button
                        type="button"
                        className="qty-btn increment"
                        onClick={handleIncrement}
                        disabled={cartPending}
                        aria-busy={cartPending}
                        aria-label="Increase quantity"
                      >
                        <i className="fa fa-plus" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  <Link
                    to={withGuestOrderTypeQuery(`/restaurants/${appName}/cart`, appName, guestOrderType)}
                    className="detail-checkout-btn detail-checkout-btn--desktop"
                  >
                    <IconCart />
                    Checkout
                    {cartCount > 0 && <span className="detail-checkout-count">{cartCount}</span>}
                  </Link>
                </>
              )}
            </div>
          </div>

          <section className="menu-item-reviews" aria-labelledby="menu-item-reviews-heading">
            <div className="reviews-section-header">
              <h2 id="menu-item-reviews-heading" className="reviews-section-title">
                Reviews
              </h2>
              <span className="review-count-label">
                {reviews.length} review{reviews.length !== 1 ? 's' : ''}
              </span>
            </div>

            {reviews.length > 0 ? (
              <ul className="review-items">
                {reviews.map((review) => (
                  <li key={review.id} className="review-item">
                    <div className="review-avatar">
                      <img
                        src={DEFAULT_USER_AVATAR}
                        alt={review.userName}
                      />
                    </div>
                    <div className="review-content">
                      <div className="review-header">
                        <div className="review-rating">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i
                              key={i}
                              className={`fa fa-star ${i < Math.round(review.rating) ? 'filled' : ''}`}
                            />
                          ))}
                        </div>
                        <h4 className="review-author">{review.userName}</h4>
                        <div className="review-date">{review.createdOn}</div>
                      </div>
                      <div className="review-comment">
                        <p>{review.comment}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="review-empty">
                <div className="review-empty-icon">
                  <i className="fa fa-star-o" aria-hidden="true" />
                </div>
                <h4>No reviews yet</h4>
                <p>Be the first to share your experience with this dish. Reviews help others decide.</p>
              </div>
            )}

            {reviewEligible === null && (
              <p className="review-eligibility-note">Checking review eligibility…</p>
            )}

            {reviewEligible === false && (
              <div className="review-cta">
                <div className="review-cta-icon">
                  <i className="fa fa-star" aria-hidden="true" />
                </div>
                <div className="review-cta-text">
                  <h5>Leave a Review</h5>
                  <p>
                    {reviewEligibilityMessage ||
                      'Only verified customers who completed an order containing this item can leave a review.'}
                    {!user && (
                      <>
                        {' '}
                        <Link to="/signin">Sign in</Link> to check eligibility.
                      </>
                    )}
                  </p>
                </div>
              </div>
            )}

            {reviewEligible && (
              <div className="review-form-card">
                <h3 className="review-form-title">Leave a Review</h3>
                <form onSubmit={handleSubmitReview} className="review-form">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Review title (optional)"
                    value={reviewForm.title}
                    onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                  />
                  <textarea
                    className="form-textarea"
                    placeholder="Share your experience with this dish"
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    required
                    rows={4}
                  />
                  <div className="rating-input-section">
                    <label htmlFor="review-rating">Rating</label>
                    <select
                      id="review-rating"
                      className="rating-select"
                      value={reviewForm.rating}
                      onChange={(e) =>
                        setReviewForm({ ...reviewForm, rating: Number(e.target.value) })
                      }
                    >
                      {[5, 4, 3, 2, 1].map((val) => (
                        <option key={val} value={val}>
                          {val} star{val !== 1 ? 's' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button type="submit" className="submit-review-btn" disabled={submittingReview}>
                    {submittingReview ? 'Submitting...' : 'Submit review'}
                  </button>
                </form>
              </div>
            )}
          </section>
        </div>
        </div>
      </PageContainer>
      {canOrder && (
      <div className="detail-mobile-fab-checkout">
        <Link
          to={withGuestOrderTypeQuery(`/restaurants/${appName}/cart`, appName, guestOrderType)}
          className="detail-fab detail-fab-cart"
          aria-label={`Go to cart${cartCount > 0 ? `, ${cartCount} items` : ''}`}
        >
          <IconCartFab />
          {cartCount > 0 && <span className="detail-fab-badge">{cartCount}</span>}
        </Link>
      </div>
      )}
      <Footer variant="restaurant" restaurant={footerRestaurant} />

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => {
          loginSuccessCallbackRef.current = null;
          setShowLoginModal(false);
        }}
        title="Sign in to continue"
        message="Sign in to save dishes to your wishlist."
        onLoginSuccess={() => {
          setShowLoginModal(false);
          const retry = loginSuccessCallbackRef.current;
          loginSuccessCallbackRef.current = null;
          if (retry) {
            setTimeout(() => retry(), 400);
          }
        }}
      />

      <MessageDialog
        isOpen={messageDialog != null}
        onClose={() => setMessageDialog(null)}
        title={messageDialog?.title}
        message={messageDialog?.message || ''}
        variant={messageDialog?.variant}
      />
    </div>
  );
};

export default MenuItemDetail;

